import type { ContributorRow } from '@/components/meal-planning/manual/ManualMealPlanAnalysisDashboard';
import {
  collectImportedTemplateIdsFromPlanRecords,
  getGenericMealPlan,
  getMealPlan,
  getMealPlanForDisplay,
  getMealPlanTemplate,
} from '@/lib/api/mealPlans';
import type { MealPlanTemplateDetails } from '@/lib/api/mealPlans';
import {
  defaultMacroTargets,
  MANUAL_ANALYSIS_MEAL_COLORS,
} from './nutritionalAnalysisConstants';
import {
  ensureDefaultEmptyRowsForMeal,
  formatMealSlotTimeForDisplay,
  normalizeItemType,
  numberFromUnknown,
  nutritionValue,
  parseRecipeServingSizesFromRecord,
  resolveIngredientServingMetadataFromRecord,
  resolveInitialRecipeServingId,
  resolveMealDisplayName,
  resolveMealTime,
  resolveRowId,
  toEditableNumber,
} from './nutritionalAnalysisPure';
import type { ExpandedFoodRow, MacroTotals, MealMeta } from './nutritionalAnalysisTypes';
import { resolveRangeMidpoint } from '@/utils/nutritionGoals';
import { extractMealPlanPreferredChoices } from '@/utils/meal-plan-preferred-choices';

/** Legacy placeholder row labels from the API or older clients — show as empty search rows. */
function resolveDisplayRowName(...candidates: (string | undefined)[]): string {
  for (const candidate of candidates) {
    const trimmed = String(candidate ?? '').trim();
    if (!trimmed) continue;
    if (/^item\s*\d+$/i.test(trimmed)) continue;
    return trimmed;
  }
  return '';
}

function flatNutritionRecordToMacroTotals(flat: Record<string, unknown>): MacroTotals {
  return {
    calories: Math.max(0, nutritionValue(flat, 'calories', 'total_calories')),
    protein: Math.max(0, nutritionValue(flat, 'protein')),
    carbs: Math.max(0, nutritionValue(flat, 'carbohydrate', 'carbs')),
    fat: Math.max(0, nutritionValue(flat, 'total_fat', 'fat')),
  };
}

type FatBreakdownKey = 'mufa' | 'pufa' | 'saturatedFat' | 'transFat';

export type FatBreakdownTotals = Record<FatBreakdownKey, number | null>;

const fatBreakdownKeys: FatBreakdownKey[] = ['mufa', 'pufa', 'saturatedFat', 'transFat'];

const FAT_BREAKDOWN_ALIASES: Record<FatBreakdownKey, string[]> = {
  mufa: ['mufa', 'monounsaturated_fat', 'monounsaturatedFat', 'monounsaturated'],
  pufa: ['pufa', 'polyunsaturated_fat', 'polyunsaturatedFat', 'polyunsaturated'],
  saturatedFat: ['saturated_fat', 'saturatedFat', 'saturated'],
  transFat: ['trans_fat', 'transFat', 'trans'],
};

function findNumericValue(record: Record<string, unknown> | null | undefined, keys: string[]): number | null {
  if (!record) return null;

  for (const key of keys) {
    if (!(key in record)) continue;
    const value = record[key];

    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) continue;
      const parsed = Number(trimmed);
      if (Number.isFinite(parsed)) return parsed;
    }
    if (value && typeof value === 'object' && 'quantity' in value) {
      const parsed = Number((value as { quantity?: unknown }).quantity);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  return null;
}

function readOptionalNutritionValue(nutrition: unknown, keys: string[]): number | null {
  if (!nutrition || typeof nutrition !== 'object') return null;
  const source = nutrition as Record<string, unknown>;
  const sourceValue = findNumericValue(source, keys);
  if (sourceValue !== null) return sourceValue;

  const macroSource =
    source.macros && typeof source.macros === 'object'
      ? (source.macros as Record<string, unknown>)
      : null;
  const macroValue = findNumericValue(macroSource, keys);
  if (macroValue !== null) return macroValue;
  return null;
}

function readTargetRangeEndpoint(value: unknown, endpoint: 'min' | 'max'): number {
  const direct = numberFromUnknown(value);
  if (direct > 0) return direct;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 0;
  const range = value as Record<string, unknown>;
  return numberFromUnknown(range[endpoint]) || numberFromUnknown(range.value);
}

function readOptionalFatBreakdownValue(nutrition: unknown, key: FatBreakdownKey): number | null {
  return readOptionalNutritionValue(nutrition, FAT_BREAKDOWN_ALIASES[key]);
}

function toStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => toStringList(entry));
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [];
}

function createEmptyFatBreakdownTotals(): FatBreakdownTotals {
  return {
    mufa: null,
    pufa: null,
    saturatedFat: null,
    transFat: null,
  };
}

/** Sums fat subtype grams from expanded rows (undefined / missing keys are skipped per subtype). */
export function sumExpandedFoodRowFatBreakdown(rows: ExpandedFoodRow[]): FatBreakdownTotals {
  const out = createEmptyFatBreakdownTotals();
  for (const key of fatBreakdownKeys) {
    let sum = 0;
    let has = false;
    for (const row of rows) {
      const raw = row[key];
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        sum += Math.max(0, raw);
        has = true;
      }
    }
    out[key] = has ? sum : null;
  }
  return out;
}

function expandedRowFatBreakdownFromNutrition(
  nutrition: Record<string, unknown>,
): Partial<Pick<ExpandedFoodRow, 'mufa' | 'pufa' | 'saturatedFat' | 'transFat'>> {
  const partial: Partial<Pick<ExpandedFoodRow, 'mufa' | 'pufa' | 'saturatedFat' | 'transFat'>> = {};
  for (const key of fatBreakdownKeys) {
    const value = readOptionalFatBreakdownValue(nutrition, key);
    if (value !== null) {
      partial[key] = Math.max(0, value);
    }
  }
  return partial;
}

function isNonEmptyNutritionObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length > 0);
}

function resolveNutritionalAnalysisItemType(
  item: Record<string, unknown>,
  ingredientIdFromApi: string,
): 'recipe' | 'ingredient' {
  const explicitItemType = String(item.itemType ?? item.type ?? '').trim().toLowerCase();
  if (explicitItemType === 'recipe') return 'recipe';
  if (explicitItemType === 'ingredient') return 'ingredient';
  if (ingredientIdFromApi.length > 0) return 'ingredient';

  const recipeIdFromApi = String(item.recipeId ?? '').trim();
  if (recipeIdFromApi.length > 0) return 'recipe';

  const hasIngredientServingMetadata =
    Array.isArray(item.possibleUnits)
    || Array.isArray(item.servingOverrides)
    || Boolean(item.ingredient && typeof item.ingredient === 'object' && !Array.isArray(item.ingredient));

  return hasIngredientServingMetadata ? 'ingredient' : normalizeItemType(item.itemType);
}

type ParsedMealEntry = {
  mealKey: string;
  mealRecord: Record<string, unknown>;
};

function toParsedMealEntries(dayMeals: unknown): ParsedMealEntry[] {
  if (Array.isArray(dayMeals)) {
    return dayMeals
      .filter((meal) => meal && typeof meal === 'object')
      .map((meal, index) => {
        const mealRecord = meal as Record<string, unknown>;
        const fallbackKey = `meal_${index + 1}`;
        const mealKey =
          String(
            mealRecord.mealName
            ?? mealRecord.name
            ?? mealRecord.mealType
            ?? fallbackKey,
          ).trim() || fallbackKey;
        return { mealKey, mealRecord };
      });
  }

  if (dayMeals && typeof dayMeals === 'object') {
    return Object.entries(dayMeals as Record<string, unknown>).map(([mealKey, mealValue], index) => ({
      mealKey: String(mealKey ?? '').trim() || `meal_${index + 1}`,
      mealRecord: (mealValue && typeof mealValue === 'object' ? mealValue : {}) as Record<string, unknown>,
    }));
  }

  return [];
}

function unwrapMealPlanPayload(rawPayload: unknown): Record<string, unknown> | null {
  if (!rawPayload || typeof rawPayload !== 'object') return null;
  const root = rawPayload as Record<string, unknown>;
  if (root.data && typeof root.data === 'object' && !Array.isArray(root.data)) {
    return root.data as Record<string, unknown>;
  }
  return root;
}

function toRawPlanDays(planRecord: Record<string, unknown> | null): Array<Record<string, unknown>> {
  if (!planRecord) return [];
  if (Array.isArray(planRecord.days)) {
    return planRecord.days
      .filter((day) => day && typeof day === 'object')
      .map((day) => {
        const dayRecord = day as Record<string, unknown>;
        return { ...dayRecord, day: dayRecord.day ?? dayRecord.dayNumber };
      });
  }
  if (Array.isArray(planRecord.daySuggestions)) {
    return planRecord.daySuggestions
      .filter((day) => day && typeof day === 'object')
      .map((day) => {
        const dayRecord = day as Record<string, unknown>;
        return { ...dayRecord, day: dayRecord.day ?? dayRecord.dayNumber };
      });
  }
  return [];
}

function templateTotalsToFlatNutrition(totals: unknown): Record<string, unknown> {
  if (!totals || typeof totals !== 'object') return {};
  const source = totals as Record<string, unknown>;
  const carbs = source.carbs ?? source.carbohydrate;
  const fat = source.fat ?? source.total_fat;
  return {
    ...source,
    calories: source.calories,
    protein: source.protein,
    carbs,
    carbohydrate: carbs,
    fat,
    total_fat: fat,
    fiber: source.fiber,
    dietary_fiber: source.fiber,
    dietary_fibre: source.fiber,
  };
}

function templateDetailToPlanRecord(template: MealPlanTemplateDetails): Record<string, unknown> {
  const days = (template.days ?? []).map((day) => ({
    ...day,
    day: day.dayNumber,
    dayNumber: day.dayNumber,
    nutrition: templateTotalsToFlatNutrition(day.totals),
    meals: (day.meals ?? []).map((meal) => ({
      ...meal,
      id: meal.mealId,
      mealName: meal.mealName,
      mealType: meal.mealType,
      mealOrder: meal.mealOrder,
      sortOrder: meal.mealOrder,
      time: meal.time,
      totalNutrition: templateTotalsToFlatNutrition(meal.totals),
      nutrition: templateTotalsToFlatNutrition(meal.totals),
      items: (meal.items ?? []).map((item) => {
        const itemRecord = item as typeof item & Record<string, unknown>;
        const itemId = String(item.itemId ?? item.id ?? '').trim();
        return {
          ...itemRecord,
          id: item.id ?? itemId,
          itemId,
          recipeName: item.name,
          title: item.name,
          source: itemRecord.source ?? itemRecord.provider ?? 'internal',
          nutrition: templateTotalsToFlatNutrition(item.nutrition),
        };
      }),
    })),
  }));

  return {
    ...template,
    status: 'template',
    name: template.templateName || template.name,
    planName: template.templateName || template.name,
    generationType: template.generationType || 'template',
    generationSource: template.generationSource || 'template',
    generationState: 'completed',
    targetDaily: {
      calories: template.totals?.calories,
      proteinMax: template.totals?.protein,
      carbsMax: template.totals?.carbs,
      fatMax: template.totals?.fat,
    },
    days,
    dailyNutrition: days.map((day) => ({
      dayNumber: day.dayNumber,
      date: day.date,
      nutrition: day.nutrition,
    })),
  };
}

const CLINICAL_TAG_ACRONYM_TOKENS = new Set(['gi', 'pcos', 'rda']);

const toClinicalTagLabel = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => {
      if (CLINICAL_TAG_ACRONYM_TOKENS.has(part.toLowerCase())) return part.toUpperCase();
      if (part.length <= 3 && part.toUpperCase() === part) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ');

function resolveClinicalTagLabels(planRecord: Record<string, unknown>): string[] {
  const rawTags = Array.isArray(planRecord.clinicalTags) ? planRecord.clinicalTags : [];
  const availableTags = Array.isArray(planRecord.availableClinicalTags) ? planRecord.availableClinicalTags : [];
  const availableLabels = new Map(
    availableTags
      .filter((tag): tag is Record<string, unknown> => Boolean(tag && typeof tag === 'object' && !Array.isArray(tag)))
      .map((tag) => [
        String(tag.id ?? tag.value ?? tag.name ?? '').trim(),
        String(tag.label ?? tag.name ?? tag.value ?? tag.id ?? '').trim(),
      ]),
  );

  return rawTags
    .map((tag) => {
      const key = String(tag ?? '').trim();
      if (!key) return '';
      return availableLabels.get(key) || toClinicalTagLabel(key);
    })
    .filter(Boolean);
}

export type NutritionalAnalysisLoadedData = {
  /** Template kind when loaded in template mode. */
  templateKind: 'meal_plan' | 'meal_slot' | null;
  planName: string;
  planStatus: string;
  planGenerationType: string;
  planGenerationSource: string;
  planGenerationState: string;
  availableDayNumbers: number[];
  dayNamesByNumber: Record<number, string>;
  effectiveDayNumber: number;
  /** Practitioner plan notes (draft `planNotes`; finalized often `description`). */
  planNotes?: string | null;
  planDescription?: string | null;
  /** Practitioner notes for the selected day. */
  dayNotes?: string | null;
  mealMeta: MealMeta[];
  rowsByMeal: Record<string, ExpandedFoodRow[]>;
  expandedCards: Record<string, boolean>;
  macroTargets: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  /**
   * Per meal display name: slot `totalNutrition` or slot `nutrition` rollup from API.
   * When a meal key is absent, UI falls back to summing rows for that meal only.
   */
  apiMealMacroTotalsByMealName: Partial<Record<string, MacroTotals>>;
  /** Selected-day totals from backend `dailyNutrition` (preferred intake source when present). */
  apiDayMacroTotals: MacroTotals | null;
  /**
   * Day-level fibre / calcium / iron / vitamin D for analysis bars.
   * When `dailyNutrition[].nutrition` exists for the day, intake uses only that flat object
   * (`dietary_fibre`, `calcium`, `iron`, `vitamin_d`, etc.); otherwise sums meal `totalNutrition` / `nutrition`.
   */
  apiNutrientTotals: {
    fiber: number;
    calcium: number;
    iron: number;
    vitaminD: number;
  };
  /** Day-level fat subtype totals from API nutrients (null means unavailable). */
  apiFatBreakdownTotals: FatBreakdownTotals;
  /** Line-item kcal from GET meal plan (same pass as rows); analysis drawer uses this instead of live grid state. */
  apiTopCalorieContributors: ContributorRow[];
  /** Intolerances applied to this plan generation (if provided by API). */
  appliedIntolerances: string[];
  /** Allergies applied to this plan generation (if provided by API). */
  appliedAllergies: string[];
  /** Cuisine types chosen for this plan generation (if provided by API). */
  appliedCuisineTypes: string[];
  /** Dietary preferences chosen for this plan generation (if provided by API). */
  appliedPreferences: string[];
  /** Clinical tags applied to generic meal plans, resolved against available tag labels when present. */
  clinicalTags: string[];
  /** True when API returned usable `targetDaily` so analysis UI uses plan macros instead of client EER/goals. */
  mealPlanTargetDailyActive: boolean;
  /** Target fields explicitly present on the loaded plan response; used for pre-analysis sidebar display only. */
  mealPlanDisplayTargetFields: {
    calories: boolean;
    protein: boolean;
    carbs: boolean;
    fat: boolean;
  };
  /** Template IDs already imported into this plan (`data.importedTemplates`); hide from import picker. */
  importedTemplateIds: string[];
  /** Template nutrition page: false when API marks template as not editable (ignored outside template mode). */
  templateCanEdit: boolean;
  /** Present in template mode for public vs private viewer layout (null outside template mode). */
  templateIsOwnTemplate: boolean | null;
  templateVisibility: string | null;
};

export async function fetchNutritionalAnalysisDayData(
  resolvedPlanId: string,
  selectedDayNumber: number,
  options: { templateMode?: boolean; genericMode?: boolean } = {},
): Promise<NutritionalAnalysisLoadedData | null> {
  const genericMode = Boolean(options.genericMode);
  const templateMode = Boolean(options.templateMode) && !genericMode;
  const templateFetch = templateMode ? await getMealPlanTemplate(resolvedPlanId).catch(() => null) : null;
  const templateDetail =
    templateFetch?.success && templateFetch.data ? templateFetch.data : null;
  const templateRecord = templateDetail ? templateDetailToPlanRecord(templateDetail) : null;
  const templateCanEditFromApi =
    !templateMode
    || (typeof templateDetail?.canEdit === 'boolean' ? templateDetail.canEdit : true);
  const templateIsOwnTemplate =
    !templateMode || !templateDetail
      ? null
      : (typeof templateDetail.isOwnTemplate === 'boolean' ? templateDetail.isOwnTemplate : null);
  const templateVisibility =
    !templateMode || !templateDetail
      ? null
      : (templateDetail.visibility != null ? String(templateDetail.visibility).trim() : null);
  const genericRawResponse = genericMode ? await getGenericMealPlan(resolvedPlanId) : null;
  if (genericMode && (!genericRawResponse?.success || !genericRawResponse.data)) {
    throw new Error(genericRawResponse?.message || 'Failed to load cohort meal plan');
  }
  const genericRawRecord =
    genericRawResponse?.success && genericRawResponse.data
      ? (genericRawResponse.data as unknown as Record<string, unknown>)
      : null;
  const genericFinalizedRawPayload = genericMode && !genericRawRecord
    ? await getMealPlan(resolvedPlanId).catch(() => null)
    : null;
  const normalizedResponse = templateMode || genericMode
      ? ({ success: false } as const)
      : await getMealPlanForDisplay(resolvedPlanId).catch(() => ({ success: false } as const));
  const rawPayload = templateMode || genericMode ? genericFinalizedRawPayload : await getMealPlan(resolvedPlanId).catch(() => null);
  const rawPlanRecord = genericMode
    ? (genericRawRecord ?? unwrapMealPlanPayload(genericFinalizedRawPayload))
    : (templateRecord ?? unwrapMealPlanPayload(rawPayload));

  const normalizedPlanRecord = normalizedResponse.success && normalizedResponse.data
    ? ({
        ...(normalizedResponse.data as unknown as Record<string, unknown>),
        days: (normalizedResponse.data.days ?? []).map((day) => ({
          ...(day as unknown as Record<string, unknown>),
          day: (day as { day?: unknown }).day ?? (day as { dayNumber?: unknown }).dayNumber,
        })),
      } as Record<string, unknown>)
    : null;

  const effectivePlanRecord = genericMode ? rawPlanRecord : (normalizedPlanRecord ?? rawPlanRecord);
  if (!effectivePlanRecord) return null;

  const planStatus = String(effectivePlanRecord.status ?? 'unknown').trim().toLowerCase() || 'unknown';
  const importedTemplateIds = genericMode
    ? collectImportedTemplateIdsFromPlanRecords(genericRawRecord, rawPlanRecord, effectivePlanRecord)
    : collectImportedTemplateIdsFromPlanRecords(
      genericRawRecord,
      normalizedPlanRecord,
      rawPlanRecord,
      effectivePlanRecord,
    );
  const planName = String(
    effectivePlanRecord.name
    ?? rawPlanRecord?.name
    ?? genericRawRecord?.name
    ?? effectivePlanRecord.planName
    ?? rawPlanRecord?.planName
    ?? genericRawRecord?.planName
    ?? effectivePlanRecord.title
    ?? rawPlanRecord?.title
    ?? genericRawRecord?.title
    ?? '',
  ).trim();
  const planCreationMethod = String(
    effectivePlanRecord.creationMethod
    ?? effectivePlanRecord.creation_method
    ?? effectivePlanRecord.creationType
    ?? effectivePlanRecord.creation_type
    ?? '',
  ).trim().toLowerCase();
  const rawPlanGenerationType = String(
    effectivePlanRecord.generationType ?? effectivePlanRecord.generation_type ?? '',
  ).trim().toLowerCase();
  const planGenerationType =
    planCreationMethod === 'generic' || planCreationMethod === 'generic_assigned'
      ? planCreationMethod
      : rawPlanGenerationType || 'unknown';
  const planGenerationSource = String(
    effectivePlanRecord.generationSource
    ?? effectivePlanRecord.generation_source
    ?? effectivePlanRecord.source
    ?? (planCreationMethod === 'generic' || planCreationMethod === 'generic_assigned' ? 'generic' : 'unknown'),
  ).trim().toLowerCase() || 'unknown';
  const planGenerationState = String(
    effectivePlanRecord.generationState ?? effectivePlanRecord.generation_state ?? 'unknown',
  ).trim().toLowerCase() || 'unknown';
  const rawTemplateKind = String(effectivePlanRecord.templateKind ?? '').trim().toLowerCase();
  const templateKind: 'meal_plan' | 'meal_slot' | null =
    rawTemplateKind === 'meal_slot' ? 'meal_slot' : rawTemplateKind === 'meal_plan' ? 'meal_plan' : null;

  const normalizedDays = Array.isArray(effectivePlanRecord.days)
    ? (effectivePlanRecord.days as Array<Record<string, unknown>>)
    : [];
  const rawDays = toRawPlanDays(rawPlanRecord);
  const candidateDays = rawDays.length > 0 ? rawDays : normalizedDays;

  const availableDayNumbers = candidateDays
    .map((day) => numberFromUnknown(day.day ?? day.dayNumber))
    .filter((dayNum) => Number.isFinite(dayNum) && dayNum > 0);
  const dayNamesByNumber = candidateDays.reduce<Record<number, string>>((acc, day) => {
    const dayNumber = numberFromUnknown(day.day ?? day.dayNumber);
    if (!Number.isFinite(dayNumber) || dayNumber <= 0) return acc;
    const rawDayName = String(day.dayName ?? '').trim();
    if (!rawDayName) return acc;
    acc[dayNumber] = rawDayName;
    return acc;
  }, {});

  const effectiveDayNumber = availableDayNumbers.includes(selectedDayNumber)
    ? selectedDayNumber
    : (availableDayNumbers[0] ?? selectedDayNumber);

  const dayData =
    candidateDays.find((day) => numberFromUnknown(day.day ?? day.dayNumber) === effectiveDayNumber) ??
    candidateDays[0];
  if (!dayData) return null;

  const dayNotesRaw = dayData.notes;
  const dayNotes =
    typeof dayNotesRaw === 'string'
      ? dayNotesRaw
      : dayNotesRaw === null
        ? null
        : undefined;

  const planNotesRaw = effectivePlanRecord.planNotes ?? effectivePlanRecord.plan_notes;
  const planNotes =
    typeof planNotesRaw === 'string'
      ? planNotesRaw
      : planNotesRaw === null
        ? null
        : undefined;
  const planDescriptionRaw = effectivePlanRecord.description;
  const planDescription =
    typeof planDescriptionRaw === 'string'
      ? planDescriptionRaw
      : planDescriptionRaw === null
        ? null
        : undefined;

  const apiMealMacroTotalsByMealName: Partial<Record<string, MacroTotals>> = {};
  let apiDayMacroTotals: MacroTotals | null = null;
  const apiNutrientTotals = { fiber: 0, calcium: 0, iron: 0, vitaminD: 0 };
  const apiFatBreakdownTotals = createEmptyFatBreakdownTotals();
  const slotFatBreakdownTotals: Record<FatBreakdownKey, number> = {
    mufa: 0,
    pufa: 0,
    saturatedFat: 0,
    transFat: 0,
  };
  const hasSlotFatBreakdownValue: Record<FatBreakdownKey, boolean> = {
    mufa: false,
    pufa: false,
    saturatedFat: false,
    transFat: false,
  };
  const rowFatBreakdownTotals: Record<FatBreakdownKey, number> = {
    mufa: 0,
    pufa: 0,
    saturatedFat: 0,
    transFat: 0,
  };
  const hasRowFatBreakdownValue: Record<FatBreakdownKey, boolean> = {
    mufa: false,
    pufa: false,
    saturatedFat: false,
    transFat: false,
  };
  const dailyFromEffective = Array.isArray(effectivePlanRecord.dailyNutrition)
    ? (effectivePlanRecord.dailyNutrition as Array<Record<string, unknown>>)
    : [];
  const dailyFromRaw = Array.isArray(rawPlanRecord?.dailyNutrition)
    ? (rawPlanRecord.dailyNutrition as Array<Record<string, unknown>>)
    : [];
  /** Prefer non-empty list so we do not skip API `dailyNutrition` when the normalized payload has `[]` but raw has rows. */
  const dailyNutritionEntries =
    dailyFromEffective.length > 0 ? dailyFromEffective : dailyFromRaw;
  const matchedDailyNutrition = dailyNutritionEntries.find(
    (entry) => numberFromUnknown(entry.dayNumber ?? entry.day) === effectiveDayNumber,
  );
  const dayNutritionFlat =
    matchedDailyNutrition?.nutrition && typeof matchedDailyNutrition.nutrition === 'object'
      ? (matchedDailyNutrition.nutrition as Record<string, unknown>)
      : null;
  /** When present, fibre + Ca/Fe/Vit D intake for nutrient-vs-goal uses only this rollup (not summed per meal). */
  const useDailyNutritionForMicroTotals = dayNutritionFlat != null;
  if (dayNutritionFlat) {
    apiDayMacroTotals = flatNutritionRecordToMacroTotals(dayNutritionFlat);
    apiNutrientTotals.fiber = Math.max(
      0,
      nutritionValue(dayNutritionFlat, 'dietary_fibre', 'dietary_fiber', 'fiber'),
    );
    apiNutrientTotals.calcium = Math.max(0, nutritionValue(dayNutritionFlat, 'calcium'));
    apiNutrientTotals.iron = Math.max(0, nutritionValue(dayNutritionFlat, 'iron'));
    apiNutrientTotals.vitaminD = Math.max(0, nutritionValue(dayNutritionFlat, 'vitamin_d', 'vitamin_d3'));
    fatBreakdownKeys.forEach((key) => {
      const value = readOptionalFatBreakdownValue(dayNutritionFlat, key);
      if (value !== null) {
        apiFatBreakdownTotals[key] = Math.max(0, value);
      }
    });
  }

  const mealEntries = toParsedMealEntries(dayData.meals).sort((left, right) => {
    const leftOrder = numberFromUnknown(left.mealRecord.mealOrder ?? left.mealRecord.sortOrder);
    const rightOrder = numberFromUnknown(right.mealRecord.mealOrder ?? right.mealRecord.sortOrder);
    if (leftOrder === rightOrder) return 0;
    if (!leftOrder) return 1;
    if (!rightOrder) return -1;
    return leftOrder - rightOrder;
  });

  const nextMealMeta: MealMeta[] = [];
  const nextRowsByMeal: Record<string, ExpandedFoodRow[]> = {};
  const apiContributorBuffer: ContributorRow[] = [];

  mealEntries.forEach(({ mealKey, mealRecord }, index) => {
    const rawMealName = resolveMealDisplayName(mealKey, index);
    const mealName =
      nextMealMeta.some((meal) => meal.name.toLowerCase() === rawMealName.toLowerCase())
        ? `${rawMealName} ${index + 1}`
        : rawMealName;
    const apiMealName = String(mealRecord.mealName ?? mealKey ?? '').trim() || mealName;

    const items =
      Array.isArray(mealRecord.items) && mealRecord.items.length
        ? (mealRecord.items as Array<Record<string, unknown>>)
        : [];
    const recipesFromSlot =
      Array.isArray(mealRecord.recipes) && mealRecord.recipes.length
        ? (mealRecord.recipes as Array<Record<string, unknown>>).map((entry) => ({
            ...entry,
            itemType: String(entry.itemType ?? '').trim() || 'recipe',
          }))
        : [];
    const ingredientItems =
      Array.isArray(mealRecord.ingredients) && mealRecord.ingredients.length
        ? (mealRecord.ingredients as Array<Record<string, unknown>>).map((entry) => ({
            ...entry,
            itemType: String(entry.itemType ?? '').trim() || 'ingredient',
          }))
        : [];
    const recipeSuggestions =
      Array.isArray(mealRecord.recipeSuggestions) && mealRecord.recipeSuggestions.length
        ? (mealRecord.recipeSuggestions as Array<Record<string, unknown>>)
        : [];
    const selectedSuggestionId = String(mealRecord.selectedRecipeId ?? '').trim();
    const selectedSuggestion =
      selectedSuggestionId.length > 0
        ? recipeSuggestions.find((suggestion) => String(suggestion.id ?? '').trim() === selectedSuggestionId)
        : undefined;
    const selectedSuggestionRows =
      selectedSuggestion != null
        ? [selectedSuggestion]
        : (recipeSuggestions.length > 0 ? [recipeSuggestions[0]] : []);
    const mergedSlotItems =
      recipesFromSlot.length || ingredientItems.length
        ? [...recipesFromSlot, ...ingredientItems]
        : [];
    const shouldPreferExplicitGenericSlotItems =
      genericMode && mergedSlotItems.length > 0;
    const recipes = shouldPreferExplicitGenericSlotItems
      ? mergedSlotItems
      : items.length
        ? items
        : mergedSlotItems.length
          ? mergedSlotItems
          : selectedSuggestionRows.length
            ? selectedSuggestionRows
            : [mealRecord];
    const rowsFromManualSlotItems = items.length > 0 || recipesFromSlot.length > 0;

    const totalNutritionRaw = mealRecord.totalNutrition;
    const slotNutritionRaw = mealRecord.nutrition;
    const slotNutritionForTotals = isNonEmptyNutritionObject(totalNutritionRaw)
      ? totalNutritionRaw
      : isNonEmptyNutritionObject(slotNutritionRaw)
        ? slotNutritionRaw
        : null;
    if (slotNutritionForTotals) {
      apiMealMacroTotalsByMealName[mealName] = flatNutritionRecordToMacroTotals(slotNutritionForTotals);
      if (!useDailyNutritionForMicroTotals) {
        apiNutrientTotals.fiber += Math.max(
          0,
          nutritionValue(slotNutritionForTotals, 'dietary_fibre', 'dietary_fiber', 'fiber'),
        );
        apiNutrientTotals.calcium += Math.max(0, nutritionValue(slotNutritionForTotals, 'calcium'));
        apiNutrientTotals.iron += Math.max(0, nutritionValue(slotNutritionForTotals, 'iron'));
        apiNutrientTotals.vitaminD += Math.max(0, nutritionValue(slotNutritionForTotals, 'vitamin_d', 'vitamin_d3'));
      }

      fatBreakdownKeys.forEach((key) => {
        const value = readOptionalFatBreakdownValue(slotNutritionForTotals, key);
        if (value === null) return;
        hasSlotFatBreakdownValue[key] = true;
        slotFatBreakdownTotals[key] += Math.max(0, value);
      });
    }

    const parsedRows = recipes.map((recipe, recipeIndex) => {
      const recipeRecord = recipe as Record<string, unknown>;
      const recipeNutrition =
        (recipeRecord.nutrition as Record<string, unknown> | undefined) ?? {};
      const calories =
        nutritionValue(recipeNutrition, 'calories', 'total_calories')
        || numberFromUnknown(recipeRecord.totalCalories)
        || numberFromUnknown(recipeRecord.calories);
      const protein = nutritionValue(recipeNutrition, 'protein');
      const carbs = nutritionValue(recipeNutrition, 'carbohydrate', 'carbs');
      const fat = nutritionValue(recipeNutrition, 'total_fat', 'fat');
      fatBreakdownKeys.forEach((key) => {
        const value = readOptionalFatBreakdownValue(recipeNutrition, key);
        if (value === null) return;
        hasRowFatBreakdownValue[key] = true;
        rowFatBreakdownTotals[key] += Math.max(0, value);
      });
      const rowName = resolveDisplayRowName(
        recipeRecord.recipeName as string | undefined,
        recipeRecord.title as string | undefined,
        recipeRecord.name as string | undefined,
      );
      const ingredientIdFromApi = String(
        recipeRecord.ingredientId ?? recipeRecord.originalIngredientId ?? '',
      ).trim();
      const ingredientServingMetadata = resolveIngredientServingMetadataFromRecord(recipeRecord);
      const itemType = resolveNutritionalAnalysisItemType(recipeRecord, ingredientIdFromApi);
      const itemIdRaw =
        (recipeRecord.itemId as string | undefined) ??
        (recipeRecord.id as string | undefined) ??
        (recipeRecord._id as string | undefined) ??
        (itemType === 'ingredient' ? ingredientIdFromApi || undefined : undefined);
      const itemId = String(itemIdRaw ?? '').trim() || undefined;
      const hasExplicitItemId = typeof recipeRecord.itemId === 'string' && recipeRecord.itemId.trim().length > 0;
      const ingredientId =
        itemType === 'ingredient'
          ? (ingredientIdFromApi || itemId)
          : undefined;
      const recipeId =
        itemType === 'recipe'
          ? (String(recipeRecord.recipeId ?? recipeRecord.id ?? '').trim() || undefined)
          : (String(recipeRecord.recipeId ?? '').trim() || undefined);
      const optionGroupId = String(recipeRecord.optionGroupId ?? '').trim() || undefined;
      const optionGroupLabel = String(recipeRecord.optionGroupLabel ?? '').trim() || undefined;
      const sourceCandidate = String(recipeRecord.source ?? '').trim().toLowerCase();
      const source: ExpandedFoodRow['source'] =
        sourceCandidate === 'internal'
          ? 'internal'
          : sourceCandidate === 'edamam'
            ? 'edamam'
            : sourceCandidate === 'spoonacular'
              ? 'spoonacular'
              : undefined;
      const sourceId = String(recipeRecord.sourceId ?? '').trim() || undefined;
      // QTY column should prefer nutritionServings whenever API provides it.
      const qty = Math.max(
        0,
        numberFromUnknown(
          itemType === 'ingredient'
            ? recipeRecord.amount ?? recipeRecord.quantity ?? recipeRecord.nutritionServings ?? recipeRecord.servings
            : recipeRecord.nutritionServings ?? recipeRecord.servings ?? recipeRecord.amount ?? recipeRecord.quantity,
        ) || 1,
      );
      const unitCandidate = recipeRecord.servingId ?? recipeRecord.unit;
      const unitFromApi =
        typeof unitCandidate === 'string' && unitCandidate.trim()
          ? unitCandidate.trim()
          : '';
      const ingredientUnitOptions = ingredientServingMetadata?.possibleUnits ?? [];
      const unit =
        itemType === 'recipe'
          ? unitFromApi
          : unitFromApi || ingredientUnitOptions[0] || '';

      const recipeServingSizes = parseRecipeServingSizesFromRecord(recipeRecord, itemType);
      const legacyServingRaw =
        typeof recipeRecord.serving === 'string' && recipeRecord.serving.trim()
          ? recipeRecord.serving.trim()
          : (typeof recipeRecord.servingId === 'string' && recipeRecord.servingId.trim()
            ? recipeRecord.servingId.trim()
            : (typeof recipeRecord.unit === 'string' && recipeRecord.unit.trim()
              ? recipeRecord.unit.trim()
              : ''));
      const serving =
        itemType === 'recipe'
          ? recipeServingSizes?.length
            ? resolveInitialRecipeServingId(recipeRecord, recipeServingSizes, legacyServingRaw)
            : resolveInitialRecipeServingId(recipeRecord, undefined, legacyServingRaw) || unitFromApi
          : legacyServingRaw;

      return {
        rowId: resolveRowId(apiMealName, recipeIndex, itemId, rowName),
        itemId,
        ...(itemType === 'recipe' && (rowsFromManualSlotItems || hasExplicitItemId) ? { fromManualSlotItem: true } : {}),
        ...(ingredientId ? { ingredientId } : {}),
        recipeId,
        itemType,
        ...(optionGroupId ? { optionGroupId } : {}),
        ...(optionGroupLabel ? { optionGroupLabel } : {}),
        ...(itemType === 'ingredient' && ingredientServingMetadata
          ? {
              ...(ingredientServingMetadata.possibleUnits.length > 0
                ? { possibleUnits: ingredientServingMetadata.possibleUnits }
                : {}),
              ...(ingredientServingMetadata.servingOverrides?.length
                ? { servingOverrides: ingredientServingMetadata.servingOverrides }
                : {}),
              ...(ingredientServingMetadata.defaultServingId
                ? { defaultServingId: ingredientServingMetadata.defaultServingId }
                : {}),
              ...(ingredientServingMetadata.defaultServingGrams != null
                ? { defaultServingGrams: ingredientServingMetadata.defaultServingGrams }
                : {}),
            }
          : {}),
        ...(recipeServingSizes?.length ? { recipeServingSizes } : {}),
        source,
        sourceId,
        item: rowName,
        qty,
        unit,
        serving,
        kcal: toEditableNumber(calories),
        protein: toEditableNumber(protein),
        carbs: toEditableNumber(carbs),
        fat: toEditableNumber(fat),
        ...expandedRowFatBreakdownFromNutrition(recipeNutrition),
        notes:
          typeof recipeRecord.notes === 'string'
            ? recipeRecord.notes
            : typeof recipeRecord.customNotes === 'string'
              ? recipeRecord.customNotes
              : recipeRecord.notes === null || recipeRecord.customNotes === null
                ? null
                : undefined,
      } as ExpandedFoodRow;
    });

    /**
     * Prefer API `daySuggestions[].meals[].totalNutrition` (then slot `nutrition`) when line items do not
     * sum to a positive kcal total — matches backend meal rollup for Calories by meal / breakdown.
     */
    const slotNutritionFlat =
      (mealRecord.totalNutrition as Record<string, unknown> | undefined) ??
      (mealRecord.nutrition as Record<string, unknown> | undefined) ??
      {};
    const sumParsedKcal = parsedRows.reduce((sum, row) => sum + numberFromUnknown(row.kcal), 0);
    let rowsForMeal = parsedRows;
    if (sumParsedKcal === 0 && recipes.length > 0) {
      const slotCal = nutritionValue(slotNutritionFlat, 'calories', 'total_calories');
      if (slotCal > 0 && rowsForMeal.length > 0) {
        const [first, ...rest] = rowsForMeal;
        rowsForMeal = [
          {
            ...first,
            kcal: toEditableNumber(nutritionValue(slotNutritionFlat, 'calories', 'total_calories')),
            protein: toEditableNumber(nutritionValue(slotNutritionFlat, 'protein')),
            carbs: toEditableNumber(nutritionValue(slotNutritionFlat, 'carbohydrate', 'carbs')),
            fat: toEditableNumber(nutritionValue(slotNutritionFlat, 'total_fat', 'fat')),
            ...expandedRowFatBreakdownFromNutrition(slotNutritionFlat),
          },
          ...rest,
        ];
      }
    }

    const mealSlotRecord = mealRecord as {
      id?: unknown;
      mealOrder?: unknown;
      sortOrder?: unknown;
      mealType?: unknown;
      mealTime?: string;
      timeSlot?: string;
      time?: string;
      targetCalories?: unknown;
      selectedOptionGroupId?: unknown;
    };
    const rawSlotTime = String(mealSlotRecord.mealTime ?? mealSlotRecord.timeSlot ?? mealSlotRecord.time ?? '').trim();
    const time = rawSlotTime ? formatMealSlotTimeForDisplay(rawSlotTime) : resolveMealTime(mealName);
    const targetCalRaw = numberFromUnknown(mealSlotRecord.targetCalories);
    const targetCalories = targetCalRaw > 0 ? targetCalRaw : null;

    const mealId = String(mealSlotRecord.id ?? '').trim() || undefined;
    const mealType = String(mealSlotRecord.mealType ?? apiMealName ?? '').trim() || undefined;
    const selectedOptionGroupId = String(mealSlotRecord.selectedOptionGroupId ?? '').trim() || undefined;

    const mealSlotNotesRaw = mealSlotRecord.notes;
    const mealSlotNotes =
      typeof mealSlotNotesRaw === 'string'
        ? mealSlotNotesRaw
        : mealSlotNotesRaw === null
          ? null
          : undefined;

    nextMealMeta.push({
      name: mealName,
      time,
      sortOrder: numberFromUnknown(mealSlotRecord.mealOrder ?? mealSlotRecord.sortOrder) || index + 1,
      apiMealName,
      ...(mealType ? { mealType } : {}),
      ...(mealId ? { mealId } : {}),
      ...(selectedOptionGroupId ? { selectedOptionGroupId } : {}),
      targetCalories,
      ...(mealSlotNotes !== undefined ? { notes: mealSlotNotes } : {}),
    });
    nextRowsByMeal[mealName] = ensureDefaultEmptyRowsForMeal(apiMealName, rowsForMeal);

    const mealColor = MANUAL_ANALYSIS_MEAL_COLORS[index % MANUAL_ANALYSIS_MEAL_COLORS.length];
    (nextRowsByMeal[mealName] ?? []).forEach((row, recipeIndex) => {
      const title = String(row.item ?? '').trim();
      if (!title) return;
      const kcal = numberFromUnknown(row.kcal);
      const rowKey = String(row.rowId ?? '').trim() || resolveRowId(apiMealName, recipeIndex, row.itemId, title);
      apiContributorBuffer.push({
        key: rowKey,
        title,
        detailLabel: [String(row.qty ?? '').trim(), String(row.unit ?? '').trim()].filter(Boolean).join(' ') || '—',
        mealLabel: mealName,
        mealColor,
        calories: Math.max(0, kcal),
        percent: 0,
        color: mealColor,
      });
    });
  });

  const nextExpandedState: Record<string, boolean> = {};
  nextMealMeta.forEach((meal, index) => {
    nextExpandedState[meal.name] = index === 0;
  });

  const targetDaily = (
    effectivePlanRecord.targetDaily && typeof effectivePlanRecord.targetDaily === 'object'
      ? effectivePlanRecord.targetDaily
      : {}
  ) as Record<string, unknown>;
  const targetCalories = numberFromUnknown(targetDaily.calories)
    || numberFromUnknown(effectivePlanRecord.targetCalories);
  const targetProtein = resolveRangeMidpoint(
    numberFromUnknown(targetDaily.proteinMin) || readTargetRangeEndpoint(effectivePlanRecord.targetProtein, 'min'),
    numberFromUnknown(targetDaily.proteinMax) || readTargetRangeEndpoint(effectivePlanRecord.targetProtein, 'max'),
  ) ?? 0;
  const targetCarbs = resolveRangeMidpoint(
    numberFromUnknown(targetDaily.carbsMin) || readTargetRangeEndpoint(effectivePlanRecord.targetCarbs, 'min'),
    numberFromUnknown(targetDaily.carbsMax) || readTargetRangeEndpoint(effectivePlanRecord.targetCarbs, 'max'),
  ) ?? 0;
  const targetFat = resolveRangeMidpoint(
    numberFromUnknown(targetDaily.fatMin) || readTargetRangeEndpoint(effectivePlanRecord.targetFat, 'min'),
    numberFromUnknown(targetDaily.fatMax) || readTargetRangeEndpoint(effectivePlanRecord.targetFat, 'max'),
  ) ?? 0;
  const mealPlanTargetDailyActive = Boolean(
    effectivePlanRecord.targetDaily
      && typeof effectivePlanRecord.targetDaily === 'object'
      && (
        numberFromUnknown(targetDaily.calories) > 0
        || numberFromUnknown(targetDaily.proteinMax) > 0
        || numberFromUnknown(targetDaily.carbsMax) > 0
        || numberFromUnknown(targetDaily.fatMax) > 0
      ),
  );
  const mealPlanDisplayTargetFields = {
    calories: targetCalories > 0,
    protein: targetProtein > 0,
    carbs: targetCarbs > 0,
    fat: targetFat > 0,
  };
  const macroTargets = {
    calories: targetCalories || defaultMacroTargets.calories,
    protein: targetProtein || defaultMacroTargets.protein,
    carbs: targetCarbs || defaultMacroTargets.carbs,
    fat: targetFat || defaultMacroTargets.fat,
  };
  const {
    appliedAllergies,
    appliedIntolerances,
    appliedCuisineTypes,
    appliedPreferences,
  } = extractMealPlanPreferredChoices(rawPlanRecord, genericRawRecord, effectivePlanRecord);
  const clinicalTags = resolveClinicalTagLabels(effectivePlanRecord);

  const apiTopCalorieContributors = [...apiContributorBuffer]
    .sort((a, b) => b.calories - a.calories)
    .slice(0, 10);

  fatBreakdownKeys.forEach((key) => {
    if (apiFatBreakdownTotals[key] != null) return;
    if (hasSlotFatBreakdownValue[key]) {
      apiFatBreakdownTotals[key] = Math.max(0, slotFatBreakdownTotals[key]);
      return;
    }
    if (hasRowFatBreakdownValue[key]) {
      apiFatBreakdownTotals[key] = Math.max(0, rowFatBreakdownTotals[key]);
    }
  });

  return {
    templateKind,
    planName,
    planStatus,
    planGenerationType,
    planGenerationSource,
    planGenerationState,
    availableDayNumbers,
    dayNamesByNumber,
    effectiveDayNumber,
    planNotes,
    planDescription,
    dayNotes,
    mealMeta: nextMealMeta,
    rowsByMeal: nextRowsByMeal,
    expandedCards: nextExpandedState,
    macroTargets,
    apiMealMacroTotalsByMealName,
    apiDayMacroTotals,
    apiNutrientTotals,
    apiFatBreakdownTotals,
    apiTopCalorieContributors,
    appliedIntolerances,
    appliedAllergies,
    appliedCuisineTypes,
    appliedPreferences,
    clinicalTags,
    mealPlanTargetDailyActive,
    mealPlanDisplayTargetFields,
    importedTemplateIds,
    templateCanEdit: templateCanEditFromApi,
    templateIsOwnTemplate,
    templateVisibility,
  };
}
